// ============================================================================
// TEAM MANAGEMENT SERVICE - Frontend
// API client for team management operations
// Feature 3: Team Management & Authorization
// ============================================================================

import { api } from './api';
import {
  Team,
  TeamMember,
  CreateTeamInput,
  UpdateTeamInput,
  AddTeamMemberInput,
  BulkAddMembersInput,
  BulkAddMembersResult,
  TeamWithMembership,
  TeamMembershipCheck
} from '../types/team.types';

// ==================== TEAM CRUD ====================

export const listTeams = async (options?: {
  status?: 'active' | 'archived';
  includeCounts?: boolean;
}): Promise<Team[]> => {
  const params = new URLSearchParams();
  if (options?.status) params.append('status', options.status);
  if (options?.includeCounts) params.append('includeCounts', 'true');

  const queryString = params.toString();
  const url = queryString ? `/api/teams?${queryString}` : '/teams';
  const response = await api.get(url);
  return response.data.teams;
};

export const getTeam = async (teamId: string): Promise<Team> => {
  const response = await api.get(`/api/teams/${teamId}`);
  return response.data.team;
};

export const createTeam = async (input: CreateTeamInput): Promise<Team> => {
  const response = await api.post('/api/teams', input);
  return response.data.team;
};

export const updateTeam = async (
  teamId: string,
  updates: UpdateTeamInput
): Promise<Team> => {
  const response = await api.patch(`/api/teams/${teamId}`, updates);
  return response.data.team;
};

export const deleteTeam = async (
  teamId: string,
  permanent?: boolean
): Promise<void> => {
  const params = permanent ? '?permanent=true' : '';
  await api.delete(`/api/teams/${teamId}${params}`);
};

export const archiveTeam = async (teamId: string): Promise<void> => {
  await deleteTeam(teamId, false);
};

// ==================== MEMBER MANAGEMENT ====================

export const getTeamMembers = async (teamId: string): Promise<TeamMember[]> => {
  const response = await api.get(`/api/teams/${teamId}/members`);
  return response.data.members;
};

export const addTeamMember = async (
  teamId: string,
  member: AddTeamMemberInput
): Promise<TeamMember> => {
  const response = await api.post(
    `/api/teams/${teamId}/members`,
    member
  );
  return response.data.member;
};

export const bulkAddTeamMembers = async (
  teamId: string,
  input: BulkAddMembersInput
): Promise<BulkAddMembersResult> => {
  const response = await api.post(
    `/api/teams/${teamId}/members/bulk`,
    input
  );
  return response.data;
};

export const removeTeamMember = async (
  teamId: string,
  userId: string
): Promise<void> => {
  await api.delete(`/api/teams/${teamId}/members/${userId}`);
};

export const updateMemberRole = async (
  teamId: string,
  userId: string,
  role: 'member' | 'lead'
): Promise<TeamMember> => {
  const response = await api.patch(
    `/api/teams/${teamId}/members/${userId}/role`,
    { role }
  );
  return response.data.member;
};

// ==================== USER TEAM MEMBERSHIP ====================

export const getUserTeams = async (userId: string): Promise<TeamWithMembership[]> => {
  const response = await api.get(`/api/teams/users/${userId}/teams`);
  return response.data.teams;
};

export const checkTeamMembership = async (
  teamId: string,
  userId: string
): Promise<TeamMembershipCheck> => {
  const response = await api.get(
    `/api/teams/${teamId}/members/${userId}/check`
  );
  return response.data;
};
